import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Flag, MoreHorizontal } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from 'components/ui/sheet';
import { cn } from 'lib/utils';
import { formatClock } from '../../constants';

interface Props {
  testTitle: string;
  sectionTitle: string;
  /** "Section 2 of 3". */
  position: string;
  /** Seconds left, or null when the section is untimed. */
  remaining: number | null;
  lowTime: boolean;
  timerHidden: boolean;
  onToggleTimer: () => void;
  saving: boolean;
  versionCode?: string;
  /** Who is sitting the test, under the test title. */
  candidateName?: string;
  /** Reading settings and full screen, already styled for the dark bar. Shown from the `sm` width up. */
  tools: React.ReactNode;
  /** What `tools` holds, as rows for the menu that replaces them on a phone. */
  menuTools: React.ReactNode;
  /** The action ends the test, not just the section. */
  finishes?: boolean;
  actionLabel: string;
  actionTitle: string;
  onAction: () => void;
  busy: boolean;
}

/** The navy bar of the computer-delivered IELTS test: test and section on the left, the clock and tools on the right. */
export function IeltsTopBar({
  testTitle,
  sectionTitle,
  position,
  remaining,
  lowTime,
  timerHidden,
  onToggleTimer,
  saving,
  versionCode,
  candidateName,
  tools,
  menuTools,
  finishes = false,
  actionLabel,
  actionTitle,
  onAction,
  busy,
}: Props) {
  const { t } = useTranslation();
  const showClock = remaining !== null && (!timerHidden || lowTime);
  const [menuOpen, setMenuOpen] = useState(false);
  const timerLabel = timerHidden ? t('exam.show_timer') : t('exam.hide_timer');
  const menuTimerLabel = timerHidden ? t('exam.menu.show_timer') : t('exam.menu.hide_timer');
  const ActionIcon = finishes ? Flag : null;

  return (
    <>
      <header className="flex h-14 shrink-0 items-center gap-x-3 bg-[#0A1628] pl-[max(0.875rem,env(safe-area-inset-left))] pr-[max(0.875rem,env(safe-area-inset-right))] text-white sm:h-16 sm:gap-x-5 sm:px-6 [@media(max-height:500px)]:sm:h-12">
        <div className="flex min-w-0 flex-1 items-center gap-3 lg:min-w-[300px] lg:flex-none">
          <span
            className="hidden h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[9px] bg-[#D1FE17] font-display text-base font-extrabold text-[#10182B] sm:flex"
            aria-hidden
          >
            E
          </span>
          <div className="min-w-0 leading-tight">
            <h1 className="truncate font-display text-[15px] font-extrabold sm:hidden">{sectionTitle}</h1>
            <p className="truncate text-sm font-bold max-sm:hidden">{testTitle}</p>
            <p className="truncate text-xs text-slate-400">
              <span className="sm:hidden">{position}</span>
              <span className="hidden sm:inline">
                {[candidateName, versionCode ? t('exam.version_code', { code: versionCode }) : null].filter(Boolean).join(' · ') || position}
              </span>
            </p>
          </div>
        </div>

        <div className="hidden min-w-[7rem] flex-1 text-center leading-tight sm:block">
          <p className="truncate font-display text-[19px] font-extrabold tracking-[-0.02em]">{sectionTitle}</p>
          <p className="truncate text-[12.5px] text-slate-400">{position}</p>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <span className="mr-1 hidden items-center gap-1.5 text-xs text-slate-400 xl:inline-flex" aria-hidden>
            <span className={cn('h-[7px] w-[7px] rounded-full', saving ? 'bg-slate-500' : 'bg-green-500')} />
            {saving ? t('exam.saving') : t('saved')}
          </span>
          <span className="hidden items-center gap-1.5 sm:flex">{tools}</span>
          {remaining !== null && (
            <>
              {showClock ? (
                <span
                  role="timer"
                  aria-label={t('exam.time_left', {
                    time: formatClock(remaining),
                  })}
                  className={cn(
                    'flex items-baseline gap-2 whitespace-nowrap rounded-[10px] px-2.5 py-1.5 font-mono text-[19px] font-bold tabular-nums sm:px-3 sm:text-[22px]',
                    lowTime ? 'bg-[#DC2626] text-white' : 'bg-white/[0.08]',
                  )}
                >
                  {formatClock(remaining)}
                  <small className={cn('font-sans text-[11.5px] font-semibold max-sm:hidden', lowTime ? 'text-red-100' : 'text-slate-400')}>
                    {t('exam.review.time_left')}
                  </small>
                </span>
              ) : (
                <>
                  <span className="hidden px-2 text-xs text-slate-400 sm:inline">{t('exam.timer_hidden')}</span>
                  <EyeOff className="h-5 w-5 text-slate-400 sm:hidden" role="img" aria-label={t('exam.timer_hidden')} />
                </>
              )}
              <button
                type="button"
                onClick={onToggleTimer}
                aria-pressed={timerHidden}
                aria-label={timerLabel}
                title={timerLabel}
                className="hidden h-[38px] w-[38px] items-center justify-center rounded-[10px] text-slate-300 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#D1FE17] sm:flex [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11"
              >
                {timerHidden ? <Eye className="h-5 w-5" aria-hidden /> : <EyeOff className="h-5 w-5" aria-hidden />}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onAction}
            disabled={busy}
            title={actionTitle}
            className="ml-1.5 hidden h-[38px] min-w-0 max-w-[16rem] items-center gap-2 rounded-[11px] [@media(pointer:coarse)]:h-11 bg-[#D1FE17] px-4 text-[13.5px] font-bold text-[#10182B] transition hover:brightness-95 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#D1FE17] disabled:opacity-50 sm:inline-flex"
          >
            {ActionIcon && <ActionIcon className="h-4 w-4 shrink-0" aria-hidden />}
            <span className="truncate">{actionLabel}</span>
          </button>
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label={t('exam.more')}
            aria-haspopup="dialog"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] bg-white/[0.08] text-white transition-colors hover:bg-white/15 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#D1FE17] sm:hidden"
          >
            <MoreHorizontal className="h-5 w-5" aria-hidden />
          </button>
        </div>
      </header>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent
          side="bottom"
          closeLabel={t('exam.nav.close')}
          closeClassName="right-2 top-2 flex h-11 w-11 items-center justify-center"
          className="max-h-[85dvh] gap-0 overflow-y-auto rounded-t-[22px] px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-5 sm:hidden"
        >
          <SheetTitle className="truncate pr-12 font-display text-lg font-extrabold">{sectionTitle}</SheetTitle>
          <SheetDescription className="mt-0.5 truncate pr-12">{position}</SheetDescription>
          <div className="mt-4">{menuTools}</div>
          {remaining !== null && (
            <button
              type="button"
              onClick={onToggleTimer}
              aria-pressed={timerHidden}
              className="mt-4 flex min-h-11 w-full items-center gap-3 rounded-xl border border-[#E6E9F2] px-3.5 text-left text-sm font-semibold text-[#10182B] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#10182B]"
            >
              {timerHidden ? <Eye className="h-5 w-5" aria-hidden /> : <EyeOff className="h-5 w-5" aria-hidden />}
              {menuTimerLabel}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              onAction();
            }}
            disabled={busy}
            className="mt-4 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#D1FE17] px-4 text-[15px] font-bold text-[#10182B] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#10182B] disabled:opacity-50"
          >
            {ActionIcon && <ActionIcon className="h-4 w-4 shrink-0" aria-hidden />}
            {actionLabel}
          </button>
        </SheetContent>
      </Sheet>

      {lowTime && (
        <p
          role="alert"
          className="shrink-0 bg-[#FDECEC] px-4 py-1.5 text-center text-xs font-semibold leading-snug text-[#7A1A12] sm:px-6 sm:py-2 sm:text-[13px]"
        >
          {t('exam.low_time')}
        </p>
      )}
    </>
  );
}

export default IeltsTopBar;
